import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Play, Pause, Heart, Music as MusicIcon, Volume2, VolumeX } from 'lucide-react';
import { ScrollReveal, staggerContainer, fadeInUp } from './ScrollAnimation';
import { motion } from 'framer-motion';

const VideoTestimonials = () => {
  const { t } = useLanguage();
  const [playingVideo, setPlayingVideo] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(4); // Start at center (index 4 for 9 videos)
  const [isMuted, setIsMuted] = useState(true); // Global mute state - muted by default
  const [userInteracted, setUserInteracted] = useState(false); // Track user interaction
  const audioRefs = useRef({});
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const touchStartTime = useRef(0);
  const autoScrollInterval = useRef(null);

  // Handle touch events for mobile swipe - AMÉLIORÉ
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartTime.current = Date.now();
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const swipeThreshold = 50;
    const diff = touchStartX.current - touchEndX.current;
    const timeDiff = Date.now() - touchStartTime.current;
    const velocity = Math.abs(diff) / timeDiff;

    // Swipe rapide ou distance suffisante
    if (Math.abs(diff) > swipeThreshold || velocity > 0.5) {
      setUserInteracted(true); // Mark user interaction
      if (diff > 0) {
        // Swiped left - go to next
        setCurrentIndex(Math.min(testimonials.length - 1, currentIndex + 1));
      } else {
        // Swiped right - go to previous
        setCurrentIndex(Math.max(0, currentIndex - 1));
      }
    }
  };

  // Handle manual navigation (Previous/Next buttons)
  const handlePrevious = () => {
    setUserInteracted(true); // Mark user interaction
    setCurrentIndex(Math.max(0, currentIndex - 1));
  };

  const handleNext = () => {
    setUserInteracted(true); // Mark user interaction
    setCurrentIndex(Math.min(testimonials.length - 1, currentIndex + 1));
  };

  // Toggle global mute
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const toggleAudio = (id) => {
    const audio = audioRefs.current[id];
    if (!audio) return;

    if (playingAudio === id) {
      audio.pause();
      setPlayingAudio(null);
    } else {
      // Pause other audios
      Object.keys(audioRefs.current).forEach(key => {
        if (audioRefs.current[key] && key !== id.toString()) {
          audioRefs.current[key].pause();
        }
      });
      audio.play();
      setPlayingAudio(id);
    }
  };

  // Mix of audio songs and video testimonials
  const testimonials = [
    {
      id: 0,
      type: 'video',
      name: 'Real Customer Reaction',
      occasion: 'Customer Testimonial',
      occasionFr: 'Témoignage Client',
      occasionEs: 'Testimonio del Cliente',
      occasionIt: 'Testimonianza del Cliente',
      occasionDe: 'Kundenbewertung',
      occasionPt: 'Depoimento do Cliente',
      thumbnail: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/5zboxkhz_image.png',
      videoUrl: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/qfj6rxpt_1er%20Reel%20%281%29.mov',
      description: 'Watch an authentic customer reaction to their personalized song',
      descriptionFr: 'Regardez une réaction authentique d\'un client à sa chanson personnalisée',
      descriptionEs: 'Mire una reacción auténtica de un cliente a su canción personalizada',
      descriptionIt: 'Guarda una reazione autentica di un cliente alla sua canzone personalizzata',
      descriptionDe: 'Sehen Sie eine authentische Kundenreaktion auf ihren personalisierten Song',
      descriptionPt: 'Assista a uma reação autêntica de um cliente à sua música personalizada',
      views: '45K',
      isNew: true
    },
    {
      id: 1,
      type: 'video',
      name: 'Customer Testimonial',
      occasion: 'Customer Review',
      occasionFr: 'Témoignage Client',
      occasionEs: 'Testimonio del Cliente',
      occasionIt: 'Testimonianza del Cliente',
      occasionDe: 'Kundenbewertung',
      occasionPt: 'Depoimento do Cliente',
      thumbnail: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/jzxkfsoe_image.png',
      videoUrl: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/w7i5xvkq_Reel%205%20%281%29.mov',
      description: 'Authentic customer reaction to their personalized song',
      descriptionFr: 'Réaction authentique d\'un client à sa chanson personnalisée',
      descriptionEs: 'Reacción auténtica de un cliente a su canción personalizada',
      descriptionIt: 'Reazione autentica di un cliente alla sua canzone personalizzata',
      descriptionDe: 'Authentische Kundenreaktion auf ihren personalisierten Song',
      descriptionPt: 'Reação autêntica de um cliente à sua música personalizada',
      views: '38K',
      isNew: true
    },
    {
      id: 2,
      type: 'video',
      name: 'Real Customer Reaction',
      occasion: 'Anniversary',
      occasionFr: 'Anniversaire',
      occasionEs: 'Aniversario',
      occasionIt: 'Anniversario',
      occasionDe: 'Jubiläum',
      occasionPt: 'Aniversário',
      thumbnail: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/jzxkfsoe_image.png',
      videoUrl: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/vnkziolk_Reel%203%20%281%29.mov',
      description: 'Watch this authentic customer reaction to their personalized song',
      descriptionFr: 'Regardez cette réaction authentique à une chanson personnalisée',
      descriptionEs: 'Mira esta reacción auténtica a una canción personalizada',
      descriptionIt: 'Guarda questa autentica reazione a una canzone personalizzata',
      descriptionDe: 'Sehen Sie diese authentische Reaktion auf ein personalisiertes Lied',
      descriptionPt: 'Assista a esta reação autêntica a uma música personalizada',
      views: '42K',
      isNew: true
    },
    {
      id: 3,
      type: 'video',
      name: 'Real Customer Testimonial',
      occasion: 'Wedding',
      occasionFr: 'Mariage',
      occasionEs: 'Boda',
      occasionIt: 'Matrimonio',
      occasionDe: 'Hochzeit',
      occasionPt: 'Casamento',
      thumbnail: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/jzxkfsoe_image.png',
      videoUrl: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/1rv8tpgr_Reel%202%20%284%29.mov',
      description: 'An unforgettable moment captured on video',
      descriptionFr: 'Un moment inoubliable capturé en vidéo',
      descriptionEs: 'Un momento inolvidable capturado en video',
      descriptionIt: 'Un momento indimenticabile catturato in video',
      descriptionDe: 'Ein unvergesslicher Moment auf Video festgehalten',
      descriptionPt: 'Um momento inesquecível capturado em vídeo',
      views: '28K',
      isNew: true
    },
    {
      id: 4,
      type: 'video',
      name: 'Customer Review',
      occasion: 'Birthday',
      occasionFr: 'Anniversaire',
      occasionEs: 'Cumpleaños',
      occasionIt: 'Compleanno',
      occasionDe: 'Geburtstag',
      occasionPt: 'Aniversário',
      thumbnail: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/jzxkfsoe_image.png',
      videoUrl: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/utz9umbt_Reel%204%20%281%29.mov',
      description: 'Pure emotion captured in this customer testimonial',
      descriptionFr: 'Émotion pure capturée dans ce témoignage client',
      descriptionEs: 'Emoción pura capturada en este testimonio de cliente',
      descriptionIt: 'Pura emozione catturata in questa testimonianza del cliente',
      descriptionDe: 'Reine Emotionen in diesem Kundenbericht festgehalten',
      descriptionPt: 'Emoção pura capturada neste depoimento de cliente',
      views: '35K',
      isNew: true
    },
    {
      id: 5,
      type: 'video',
      name: 'Real Customer Reaction',
      occasion: 'Customer Testimonial',
      occasionFr: 'Témoignage Client',
      occasionEs: 'Testimonio del Cliente',
      occasionIt: 'Testimonianza del Cliente',
      occasionDe: 'Kundenbewertung',
      occasionPt: 'Depoimento do Cliente',
      thumbnail: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/uuru1qgl_Reel%201.mov#t=3',
      videoUrl: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/uuru1qgl_Reel%201.mov',
      description: 'Watch an authentic customer reaction to their personalized song',
      descriptionFr: 'Regardez une réaction authentique d\'un client à sa chanson personnalisée',
      descriptionEs: 'Mire una reacción auténtica de un cliente a su canción personalizada',
      descriptionIt: 'Guarda una reazione autentica di un cliente alla sua canzone personalizzata',
      descriptionDe: 'Sehen Sie eine authentische Kundenreaktion auf ihren personalisierten Song',
      descriptionPt: 'Assista a uma reação autêntica de um cliente à sua música personalizada',
      views: '42K',
      isNew: true
    },
    {
      id: 6,
      type: 'video',
      name: 'Real Customer Reaction',
      occasion: 'Customer Testimonial',
      occasionFr: 'Témoignage Client',
      occasionEs: 'Testimonio del Cliente',
      occasionIt: 'Testimonianza del Cliente',
      occasionDe: 'Kundenbewertung',
      occasionPt: 'Depoimento do Cliente',
      thumbnail: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/qio661sd_Reel%207%20%281%29.mov#t=3',
      videoUrl: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/qio661sd_Reel%207%20%281%29.mov',
      description: 'Watch an authentic customer reaction to their personalized song',
      descriptionFr: 'Regardez une réaction authentique d\'un client à sa chanson personnalisée',
      descriptionEs: 'Mire una reacción auténtica de un cliente a su canción personalizada',
      descriptionIt: 'Guarda una reazione autentica di un cliente alla sua canzone personalizzata',
      descriptionDe: 'Sehen Sie eine authentische Kundenreaktion auf ihren personalisierten Song',
      descriptionPt: 'Assista a uma reação autêntica de um cliente à sua música personalizada',
      views: '38K',
      isNew: true
    },
    {
      id: 7,
      type: 'video',
      name: 'Real Customer Reaction',
      occasion: 'Customer Testimonial',
      occasionFr: 'Témoignage Client',
      occasionEs: 'Testimonio del Cliente',
      occasionIt: 'Testimonianza del Cliente',
      occasionDe: 'Kundenbewertung',
      occasionPt: 'Depoimento do Cliente',
      thumbnail: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/od2rjnat_Reel%208%20%281%29.mov#t=3',
      videoUrl: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/od2rjnat_Reel%208%20%281%29.mov',
      description: 'Watch an authentic customer reaction to their personalized song',
      descriptionFr: 'Regardez une réaction authentique d\'un client à sa chanson personnalisée',
      descriptionEs: 'Mire una reacción auténtica de un cliente a su canción personalizada',
      descriptionIt: 'Guarda una reazione autentica di un cliente alla sua canzone personalizzata',
      descriptionDe: 'Sehen Sie eine authentische Kundenreaktion auf ihren personalisierten Song',
      descriptionPt: 'Assista a uma reação autêntica de um cliente à sua música personalizada',
      views: '51K',
      isNew: true
    },
    {
      id: 8,
      type: 'video',
      name: 'Real Customer Reaction',
      occasion: 'Customer Testimonial',
      occasionFr: 'Témoignage Client',
      occasionEs: 'Testimonio del Cliente',
      occasionIt: 'Testimonianza del Cliente',
      occasionDe: 'Kundenbewertung',
      occasionPt: 'Depoimento do Cliente',
      thumbnail: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/zq6wygod_Reel%209%20%281%29.mov#t=3',
      videoUrl: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/zq6wygod_Reel%209%20%281%29.mov',
      description: 'Watch an authentic customer reaction to their personalized song',
      descriptionFr: 'Regardez une réaction authentique d\'un client à sa chanson personnalisée',
      descriptionEs: 'Mire una reacción auténtica de un cliente a su canción personalizada',
      descriptionIt: 'Guarda una reazione autentica di un cliente alla sua canzone personalizzata',
      descriptionDe: 'Sehen Sie eine authentische Kundenreaktion auf ihren personalisierten Song',
      descriptionPt: 'Assista a uma reação autêntica de um cliente à sua música personalizada',
      views: '47K',
      isNew: true
    }
  ];

  // Auto-scroll effect: scroll every 5 seconds unless user interacts
  useEffect(() => {
    // Clear any existing interval
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
    }

    // If user has interacted, don't auto-scroll
    if (userInteracted) {
      return;
    }

    // Set up auto-scroll interval
    autoScrollInterval.current = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        // If at the end, go back to start
        if (prevIndex >= testimonials.length - 1) {
          return 0;
        }
        // Otherwise, go to next
        return prevIndex + 1;
      });
    }, 5000); // 5 seconds

    // Cleanup on unmount
    return () => {
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
      }
    };
  }, [userInteracted, testimonials.length]);

  const { language } = useLanguage();

  const getOccasion = (testimonial) => {
    if (language === 'fr' && testimonial.occasionFr) return testimonial.occasionFr;
    if (language === 'es' && testimonial.occasionEs) return testimonial.occasionEs;
    if (language === 'it' && testimonial.occasionIt) return testimonial.occasionIt;
    if (language === 'de' && testimonial.occasionDe) return testimonial.occasionDe;
    if (language === 'pt' && testimonial.occasionPt) return testimonial.occasionPt;
    return testimonial.occasion;
  };

  const getDescription = (testimonial) => {
    if (language === 'fr' && testimonial.descriptionFr) return testimonial.descriptionFr;
    if (language === 'es' && testimonial.descriptionEs) return testimonial.descriptionEs;
    if (language === 'it' && testimonial.descriptionIt) return testimonial.descriptionIt;
    if (language === 'de' && testimonial.descriptionDe) return testimonial.descriptionDe;
    if (language === 'pt' && testimonial.descriptionPt) return testimonial.descriptionPt;
    return testimonial.description;
  };

  const handlePlayVideo = (id) => {
    setPlayingVideo(id);
  };

  return (
    <section id="examples" className="py-20 section-wrapper relative overflow-hidden">
      {/* Gradient Background similar to Melodfy */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-100 via-pink-100 to-rose-200"></div>
      
      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Section Header */}
        <ScrollReveal variant="scaleIn">
          <div className="text-center mb-16">
            {/* Heart Icon */}
            <div className="inline-block mb-4">
              <Heart className="w-12 h-12 text-pink-600 mx-auto" fill="currentColor" />
            </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('testimonials.title').split(' ').map((word, i) => 
              (word === 'Reactions' || word === 'Réactions' || word === 'Reacciones' || word === 'Reazioni') ? 
                <span key={i} className="bg-gradient-to-r from-purple-600 via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">{word} </span> : 
                word + ' '
            )}
          </h2>
          <p className="text-gray-700 max-w-3xl mx-auto text-lg leading-relaxed">
            <span className="font-semibold">{t('testimonials.lived')}</span> {t('testimonials.subtitle')}
          </p>
        </div>
        </ScrollReveal>

        {/* Carousel of Videos - 5 ÉCRANS VISIBLES EN ÉVENTAIL */}
        <ScrollReveal variant="fadeInUp" delay={0.3}>
          <div 
            className="relative h-[500px] sm:h-[550px] md:h-[700px] flex items-center justify-center mb-8 select-none"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ touchAction: 'pan-y pinch-zoom' }}
          >
          <div className="relative w-full max-w-6xl mx-auto flex items-center justify-center" style={{ perspective: '1500px' }}>
            {testimonials.slice(currentIndex, currentIndex + 5).map((testimonial, index) => {
              // Calculate position and rotation for fan effect - 5 CARTES
              const totalCards = Math.min(5, testimonials.slice(currentIndex, currentIndex + 5).length);
              const middleIndex = Math.floor(totalCards / 2);
              const offset = index - middleIndex;
              const rotation = offset * 8; // degrees of rotation
              
              // Responsive spacing - smaller on mobile
              const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
              const translateX = offset * (isMobile ? 80 : 150); // RÉDUIT sur mobile
              const translateZ = -Math.abs(offset) * 30; // depth effect RÉDUIT
              const scale = 1 - Math.abs(offset) * 0.08; // scale effect
              const zIndex = totalCards - Math.abs(offset);
              const opacity = 1 - Math.abs(offset) * 0.1; // fade sides RÉDUIT pour plus de visibilité
              
              return (
                <div
                  key={testimonial.id}
                  className="absolute transition-all duration-700 ease-out cursor-pointer hover:scale-110 hover:z-50"
                  style={{
                    transform: `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotation}deg) scale(${scale})`,
                    zIndex: zIndex,
                    opacity: opacity,
                    transformStyle: 'preserve-3d'
                  }}
                >
                  <Card className="w-56 sm:w-64 bg-black rounded-3xl overflow-hidden shadow-2xl border-4 sm:border-8 border-gray-900">
                    <CardContent className="p-0">
                      {/* Smartphone-style Video Card */}
                      <div className="relative h-[420px] sm:h-[500px] bg-gray-900">
                        {testimonial.type === 'audio' ? (
                          // Audio Player
                          <div 
                            className="relative h-full flex flex-col items-center justify-center p-6"
                            style={{
                              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${testimonial.thumbnail})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center'
                            }}
                          >
                            <audio
                              ref={(el) => (audioRefs.current[testimonial.id] = el)}
                              src={testimonial.audioUrl}
                              onEnded={() => setPlayingAudio(null)}
                            />
                            
                            <MusicIcon className="w-16 h-16 text-white mb-6 drop-shadow-2xl" />
                            
                            <button
                              onClick={() => toggleAudio(testimonial.id)}
                              className="w-20 h-20 rounded-full bg-gradient-to-r from-purple-600 via-violet-500 to-fuchsia-500 text-white flex items-center justify-center hover:scale-110 transition-all shadow-2xl mb-4"
                            >
                              {playingAudio === testimonial.id ? (
                                <Pause className="w-10 h-10" fill="white" />
                              ) : (
                                <Play className="w-10 h-10 ml-1" fill="white" />
                              )}
                            </button>
                            
                            <p className="text-sm text-white font-semibold drop-shadow-lg text-center">
                              {playingAudio === testimonial.id ? t('testimonials.playing') : t('testimonials.listen')}
                            </p>
                            
                            {/* Stars Rating */}
                            <div className="absolute bottom-4 flex gap-1">
                              {[...Array(5)].map((_, i) => (
                                <span key={i} className="text-yellow-400 text-lg">★</span>
                              ))}
                            </div>
                          </div>
                        ) : playingVideo === testimonial.id ? (
                          // Video Player - Native HTML5 pour .mov, iframe pour YouTube
                          testimonial.videoUrl.includes('youtube.com') || testimonial.videoUrl.includes('youtu.be') ? (
                            <iframe
                              src={testimonial.videoUrl}
                              title={testimonial.name}
                              className="w-full h-full"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          ) : (
                            <video
                              src={testimonial.videoUrl}
                              className="w-full h-full object-cover"
                              controls
                              autoPlay
                              loop
                              playsInline
                              muted={false}
                            />
                          )
                        ) : (
                          // Video Thumbnail - Joue en boucle, son selon état global mute
                          <div className="relative h-full">
                            {testimonial.videoUrl.includes('.mov') || testimonial.videoUrl.includes('.mp4') ? (
                              // Vidéo native en autoplay (son contrôlé par état global isMuted)
                              <video
                                src={testimonial.videoUrl}
                                className="w-full h-full object-cover"
                                autoPlay
                                loop
                                playsInline
                                muted={index !== currentIndex || isMuted}
                                preload="auto"
                                onClick={() => handlePlayVideo(testimonial.id)}
                              />
                            ) : (
                              // Image statique pour les autres types
                              <img
                                src={testimonial.thumbnail}
                                alt={testimonial.name}
                                className="w-full h-full object-cover"
                              />
                            )}
                            
                            {/* Overlay transparent - Click pour ouvrir en plein écran */}
                            <div 
                              className="absolute inset-0 cursor-pointer hover:bg-black/10 transition-colors"
                              onClick={() => handlePlayVideo(testimonial.id)}
                            >
                            </div>

                            {/* Views Badge */}
                            {testimonial.views && (
                              <div className="absolute top-2 sm:top-3 right-2 sm:right-3">
                                <Badge className="bg-black/70 text-white border-0 backdrop-blur text-[10px] sm:text-xs px-2 py-0.5">
                                  {testimonial.views} {t('common.views')}
                                </Badge>
                              </div>
                            )}

                            {/* Occasion Badge */}
                            <div className="absolute bottom-12 sm:bottom-16 left-1/2 transform -translate-x-1/2">
                              <Badge className="bg-gradient-to-r from-pink-500 to-rose-500 text-white border-0 text-[10px] sm:text-xs px-2 py-1">
                                {getOccasion(testimonial)}
                              </Badge>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
          
          {/* Navigation Arrows - AMÉLIORÉS Plus visibles et réactifs */}
          <motion.button 
            onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
            whileHover={{ scale: 1.15, x: -5 }}
            whileTap={{ scale: 0.95 }}
            className={`absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-600 via-violet-500 to-fuchsia-500 text-white shadow-2xl hover:shadow-purple-500/50 transition-all flex items-center justify-center z-50 backdrop-blur-sm ${currentIndex === 0 ? 'opacity-20 cursor-not-allowed' : 'opacity-95 hover:opacity-100'}`}
          >
            <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </motion.button>
          <motion.button 
            onClick={() => setCurrentIndex(Math.min(testimonials.length - 1, currentIndex + 1))}
            disabled={currentIndex >= testimonials.length - 1}
            whileHover={{ scale: 1.15, x: 5 }}
            whileTap={{ scale: 0.95 }}
            className={`absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-purple-600 via-violet-500 to-fuchsia-500 text-white shadow-2xl hover:shadow-purple-500/50 transition-all flex items-center justify-center z-50 backdrop-blur-sm ${currentIndex >= testimonials.length - 1 ? 'opacity-20 cursor-not-allowed' : 'opacity-95 hover:opacity-100'}`}
          >
            <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </motion.button>
          
          {/* Pagination Dots + Mute Button - Grouped */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-3">
            {/* Pagination Dots */}
            <div className="flex gap-2 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full shadow-lg">
            {testimonials.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentIndex(index)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                className={`rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'w-8 h-3 bg-gradient-to-r from-purple-600 to-fuchsia-500' 
                    : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
            </div>
            
            {/* Mute/Unmute Button */}
            <motion.button
              onClick={toggleMute}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="w-12 h-12 rounded-full bg-white/90 backdrop-blur-md shadow-lg flex items-center justify-center hover:bg-white transition-all ml-2"
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <VolumeX className="w-6 h-6 text-gray-700" />
              ) : (
                <Volume2 className="w-6 h-6 text-purple-600" />
              )}
            </motion.button>

          </div>
          </div>
        </ScrollReveal>

        {/* Call to Action Button - Super Design */}
        <ScrollReveal variant="scaleIn" delay={0.5}>
          <div className="text-center">
          <a
            href="#create"
            className="group relative inline-flex items-center justify-center px-12 py-5 text-lg font-bold text-white bg-gradient-to-r from-purple-600 via-violet-500 to-fuchsia-500 rounded-full shadow-2xl hover:shadow-purple-500/50 transform hover:scale-110 transition-all duration-300 overflow-hidden"
          >
            {/* Animated shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            
            {/* Glow effect on hover */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-400 to-fuchsia-400 opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300"></div>
            
            {/* Button text */}
            <span className="relative z-10 flex items-center gap-2">
              {t('testimonials.ctaButton')} 
              <span className="text-2xl group-hover:translate-x-1 transition-transform duration-300">→</span>
            </span>
            
            {/* Pulse animation */}
            <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping opacity-75"></div>
          </a>
        </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default VideoTestimonials;
